import { FC, ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuthContext } from '@/contexts/AuthContext';
import adminAgentsService from '@/services/adminAgents.service';
import assistantQuotaService from '@/services/assistantQuota.service';
import Footer from './Footer';

interface AdminLayoutProps {
  children: ReactNode;
}

interface AdminLink {
  path: string;
  label: string;
  icon: string;
  adminOnly?: boolean;
  /** Chave do badge de contagem (P12 — `pubs` ou `quotas`). */
  badgeKey?: 'pubs' | 'quotas';
}

const allAdminLinks: AdminLink[] = [
  { path: '/admin', label: 'Dashboard', icon: 'dashboard' },
  { path: '/admin/relatorios', label: 'Relatórios', icon: 'analytics', adminOnly: true },
  { path: '/admin/custos-agentes', label: 'Custos de Agentes', icon: 'payments', adminOnly: true },
  { path: '/admin/cursos', label: 'Cursos', icon: 'menu_book' },
  { path: '/admin/tags', label: 'Tags', icon: 'label' },
  { path: '/admin/agentes', label: 'Agentes', icon: 'smart_toy' },
  { path: '/admin/agentes/aprovacoes', label: 'Aprovações', icon: 'fact_check', badgeKey: 'pubs' },
  {
    path: '/admin/agentes/pedidos',
    label: 'Pedidos cota',
    icon: 'request_quote',
    adminOnly: true,
    badgeKey: 'quotas',
  },
  { path: '/admin/assistente', label: 'Assistente (H.Bot)', icon: 'auto_awesome', adminOnly: true },
  { path: '/admin/badges', label: 'Badges', icon: 'military_tech' },
  { path: '/admin/usuarios', label: 'Usuários', icon: 'group', adminOnly: true },
  { path: '/admin/missoes', label: 'Missões', icon: 'flag' },
  { path: '/admin/niveis', label: 'Níveis', icon: 'trending_up' },
  { path: '/admin/temporadas', label: 'Temporadas', icon: 'emoji_events' },
  { path: '/admin/setores', label: 'Setores', icon: 'domain', adminOnly: true },
  { path: '/admin/notificacoes', label: 'Notificações', icon: 'campaign' },
];

const POLL_MS = 60_000;

const AdminLayout: FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuthContext();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [pendingPubs, setPendingPubs] = useState(0);
  const [pendingQuotas, setPendingQuotas] = useState(0);

  const adminLinks = user?.role === 'ADMIN'
    ? allAdminLinks
    : allAdminLinks.filter(link => !link.adminOnly);

  // Polling 60s das contagens pra badges no sidebar. Best-effort (silencia
  // erros — o sidebar não pode quebrar por causa de uma falha de fetch).
  useEffect(() => {
    if (!user) return;
    const isAdmin = user.role === 'ADMIN';
    let alive = true;
    const fetchCounts = async () => {
      try {
        const pubs = await adminAgentsService.listPublicationRequests({ status: 'PENDING' });
        if (alive) setPendingPubs(pubs.length);
      } catch {
        /* noop — badge fica como estava */
      }
      if (!isAdmin) return;
      try {
        // A fila admin junta os pedidos do Hub e do Assistente; o badge soma os dois.
        const [hub, assistant] = await Promise.all([
          adminAgentsService.listQuotaIncreaseRequests({ status: 'PENDING' }).catch(() => []),
          assistantQuotaService.listIncreaseRequests({ status: 'PENDING' }).catch(() => []),
        ]);
        if (alive) setPendingQuotas(hub.length + assistant.length);
      } catch {
        /* noop */
      }
    };
    void fetchCounts();
    const id = setInterval(() => void fetchCounts(), POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [user]);

  const isActive = (path: string) => {
    if (path === '/admin') return location.pathname === '/admin';
    // /admin/agentes não pode marcar /admin/agentes/aprovacoes como ativo
    // (e vice-versa) — comparação exata pra paths irmãos sob /admin/agentes.
    if (path === '/admin/agentes') return location.pathname === '/admin/agentes';
    return location.pathname.startsWith(path);
  };

  const badgeFor = (key?: 'pubs' | 'quotas'): number => {
    if (key === 'pubs') return pendingPubs;
    if (key === 'quotas') return pendingQuotas;
    return 0;
  };

  return (
    <div className="min-h-screen bg-surface-primary text-content-primary flex">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="fixed top-4 left-4 z-30 lg:hidden bg-accent p-3 rounded-xl shadow-lg"
        aria-label="Abrir menu admin"
      >
        <span className="material-symbols-outlined text-white">menu</span>
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Admin Sidebar — posição controlada só pelo template literal
          (ver Sidebar.tsx para o racional do conflito sticky/fixed). */}
      <aside className={`border-r border-white/10 flex flex-col h-screen bg-sidebar-bg w-64 z-50 transition-all duration-300 ${
        isMobileOpen
          ? 'fixed left-0 top-0'
          : 'fixed left-[-100%] lg:left-0 lg:sticky lg:top-0'
      }`}>
        <div className="p-6 flex flex-col h-full">
          <div className="flex items-center gap-3 mb-10">
            <div className="bg-orange-500 p-2 rounded-lg shrink-0">
              <span className="material-symbols-outlined text-white">admin_panel_settings</span>
            </div>
            <div className="flex flex-col">
              <h1 className="text-white text-sm font-bold leading-tight">Painel</h1>
              <p className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Admin</p>
            </div>
          </div>

          <nav className="flex-1 space-y-2 overflow-y-auto">
            {adminLinks.map((link) => {
              const count = badgeFor(link.badgeKey);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive(link.path)
                      ? 'bg-sidebar-active/10 text-sidebar-active border border-sidebar-active/20'
                      : 'text-slate-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <span className="material-symbols-outlined">{link.icon}</span>
                  <span className="font-medium flex-1">{link.label}</span>
                  {count > 0 && (
                    <span
                      data-testid={`badge-${link.badgeKey}`}
                      className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-accent text-surface-primary text-xs font-bold"
                    >
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="mt-auto pt-6 border-t border-white/10 space-y-2">
            <button
              onClick={toggleTheme}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:text-white w-full transition-all"
            >
              <span className="material-symbols-outlined">
                {theme === 'dark' ? 'light_mode' : 'dark_mode'}
              </span>
              <span className="font-medium">
                {theme === 'dark' ? 'Tema Claro' : 'Tema Escuro'}
              </span>
            </button>
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:text-white transition-all"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="font-medium">Voltar ao h.lab</span>
            </Link>
          </div>
        </div>
      </aside>

      {/* pt-16 no mobile reserva espaço para o botão hamburger fixo */}
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto pt-16 lg:pt-0">
        {children}
        <Footer />
      </main>
    </div>
  );
};

export default AdminLayout;
